import React, { useEffect, useCallback } from 'react'
import { StyledButtonIcon, StyledControlsWrapper } from './VideoControl.style'
import { IconButton } from '@wsc_sports/components-library'

export interface Props {
  onPlayPause: () => void
  isPlaying: boolean
  videoRef: React.RefObject<HTMLVideoElement>
  duration: number
  setCurrentTime: React.Dispatch<React.SetStateAction<number>>
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>
}

// For "back and forward" buttons:
// videos up to 30 seconds long, the clicks will pass 2 seconds,
// and if it's over 30 seconds long, the buttons will skip 10 seconds
const FAST_FORWARD_BACKWARD_SMALL_STEP = 2
const FAST_FORWARD_BACKWARD_BIG_STEP = 10

const getJumpingTime = (duration: number): number => {
  return duration / 60 > 0.5 ? FAST_FORWARD_BACKWARD_BIG_STEP : FAST_FORWARD_BACKWARD_SMALL_STEP
}

const VideoControl = ({
  onPlayPause,
  isPlaying,
  videoRef,
  duration,
  setCurrentTime,
}: Props): JSX.Element => {
  const onRewind = useCallback(() => {
    if (videoRef.current) {
      const jumpingTime = getJumpingTime(videoRef.current.duration)

      const newCurrentTime = Math.max(0, videoRef.current.currentTime - jumpingTime)

      videoRef.current.currentTime = newCurrentTime
      setCurrentTime(newCurrentTime)
    }
  }, [videoRef, setCurrentTime])

  const onFastForward = useCallback(() => {
    if (videoRef.current) {
      const jumpingTime = getJumpingTime(videoRef.current.duration)

      const newCurrentTime = Math.min(duration, videoRef.current.currentTime + jumpingTime)

      videoRef.current.currentTime = newCurrentTime
      setCurrentTime(newCurrentTime)
    }
  }, [videoRef, duration, setCurrentTime])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'ArrowRight') {
        onFastForward()
      } else if (event.code === 'ArrowLeft') {
        onRewind()
      } else if (event.code === 'Space') {
        onPlayPause()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onFastForward, onRewind, onPlayPause])

  return (
    <StyledControlsWrapper>
      <StyledButtonIcon onClick={onRewind}>
        <IconButton
          name="fast_rewind"
          //TODO: Consult with designers what should be the IconButton type here and replace
          type="UNSAFE_transparent"
          isFill={true}
          title="Rewind"
        />
      </StyledButtonIcon>
      <StyledButtonIcon onClick={onPlayPause}>
        {isPlaying ? (
          <IconButton
            name="pause"
            //TODO: Consult with designers what should be the IconButton type here and replace
            type="UNSAFE_transparent"
            isFill={true}
            title="Pause"
          />
        ) : (
          <IconButton
            name="play_arrow"
            //TODO: Consult with designers what should be the IconButton type here and replace
            type="UNSAFE_transparent"
            isFill={true}
            title="Play"
          />
        )}
      </StyledButtonIcon>
      <StyledButtonIcon onClick={onFastForward}>
        <IconButton
          name="fast_forward"
          //TODO: Consult with designers what should be the IconButton type here and replace
          type="UNSAFE_transparent"
          isFill={true}
          title="Fast forward"
        />
      </StyledButtonIcon>
    </StyledControlsWrapper>
  )
}

export default VideoControl
